import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Grid, FormHelperText } from "@mui/material";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import FormField from "./FormField";
import { ranuButtonSx, ranuOutlinedButtonSx } from "./SectionCard";
import {
  confirmAction,
  digitsOnly,
  getFormErrors,
  getInitialTouched,
  hasErrors,
  labelOf,
} from "./FormUtil";

// Render order, rules and placeholders of the form
const FIELDS = [
  { name: "title" },
  { name: "phone", placeholder: "Enter phone number" },
  { name: "address_line_1", full: true },
  { name: "address_line_2", placeholder: "Enter address line 2 (optional)", full: true },
  { name: "city", required: true },
  { name: "state" },
  { name: "country" },
  { name: "pincode", placeholder: "Enter 6-digit pincode" },
];

// Digits-only fields and their maximum length
const DIGIT_LIMITS = { phone: 10, pincode: 6 };

const AddressForm = ({ addressData, onSubmit, onUndoChanges, isEdit, isConfirmed }) => {
  const [formData, setFormData] = useState(addressData);
  const [hasChanges, setHasChanges] = useState(false);
  const [touched, setTouched] = useState({});

  const resetForm = useCallback(() => {
    setFormData(addressData);
    setHasChanges(false);
    setTouched(getInitialTouched(getFormErrors(FIELDS, addressData), addressData, isEdit));
  }, [addressData, isEdit]);

  // Reset the form whenever a different address is loaded
  useEffect(() => {
    resetForm();
  }, [resetForm]);

  /* ------------------------------- Validation ------------------------------ */
  // Real validation state: used to enable / disable the submit button
  const errors = getFormErrors(FIELDS, formData);
  const isFormValid = !hasErrors(errors);

  // What the user sees: an error only appears once the field has been touched
  const getVisibleError = (name) => (touched[name] ? errors[name] : "");

  /* -------------------------------- Handlers ------------------------------- */
  const handleChange = (field, value) => {
    const nextValue = field in DIGIT_LIMITS ? digitsOnly(value, DIGIT_LIMITS[field]) : value;
    setFormData((prev) => ({ ...prev, [field]: nextValue }));
    setHasChanges(true);
  };

  const handleBlur = (field) => setTouched((prev) => ({ ...prev, [field]: true }));

  const handleUndo = () => {
    resetForm();
    onUndoChanges();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Nothing changed on an old saved address: just confirm, no validation / API call
    if (!hasChanges && isEdit) {
      onSubmit(formData, hasChanges);
      return;
    }

    // Show every error (if any) when the user tries to submit
    if (!isFormValid) {
      const allTouched = {};
      FIELDS.forEach(({ name }) => {
        allTouched[name] = true;
      });
      setTouched(allTouched);
      return;
    }

    const confirmed = await confirmAction({
      title: isEdit ? "Update Address?" : "Add New Address?",
      text: isEdit
        ? "Are you sure you want to update this address?"
        : "Are you sure you want to add this new address?",
    });

    if (confirmed) {
      onSubmit(formData, hasChanges);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate autoComplete="off">
      <Grid container spacing={2}>
        {FIELDS.map(({ name, required, placeholder, full }) => {
          const error = getVisibleError(name);

          return (
            <FormField key={name} name={name} required={required} full={full}>
              <MDInput
                type="text"
                autoComplete="off"
                value={formData[name] ?? ""}
                onChange={(e) => handleChange(name, e.target.value)}
                onBlur={() => handleBlur(name)}
                error={!!error}
                fullWidth
                disabled={isConfirmed}
                placeholder={placeholder ?? `Enter ${labelOf(name)}`}
              />
              {error && <FormHelperText error>{error}</FormHelperText>}
            </FormField>
          );
        })}

        <Grid item xs={12}>
          <MDBox
            display="flex"
            justifyContent={{ xs: "center", sm: "flex-end" }}
            flexWrap="wrap"
            gap={2}
          >
            {hasChanges && !isEdit && (
              <MDButton
                type="button"
                variant="outlined"
                color="error"
                onClick={handleUndo}
                sx={ranuOutlinedButtonSx}
              >
                Cancel
              </MDButton>
            )}

            <MDButton type="submit" color="primary" disabled={isConfirmed} sx={ranuButtonSx}>
              {isEdit ? (hasChanges ? "Update Address" : "Confirm") : "Add Address"}
            </MDButton>
          </MDBox>
        </Grid>
      </Grid>
    </form>
  );
};

AddressForm.propTypes = {
  addressData: PropTypes.shape({
    title: PropTypes.string,
    phone: PropTypes.string,
    address_line_1: PropTypes.string,
    address_line_2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    country: PropTypes.string,
    pincode: PropTypes.string,
    is_default: PropTypes.oneOf([0, 1]),
  }).isRequired,
  onSubmit: PropTypes.func.isRequired,
  onUndoChanges: PropTypes.func.isRequired,
  isEdit: PropTypes.bool.isRequired,
  isConfirmed: PropTypes.bool.isRequired,
};

export default AddressForm;
